import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Undo2, Trash2, User, UserCheck, UserX, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api'; // Update this with your actual API base URL

const DeletedMembersPage = () => {
  const [deletedMembers, setDeletedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [restoring, setRestoring] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [opdSubCity, setOpdSubCity] = useState('');
  const navigate = useNavigate();

  // Load current user and deleted members
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user?.email) {
      setCurrentUserEmail(user.email);
      fetchDeletedMembers(user.email);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const fetchDeletedMembers = async (email) => {
  try {
    setLoading(true);
    setError('');
    
    // Fetch OPD profile to get sub city
    let subCity = opdSubCity;
    if (!subCity) {
      const profRes = await fetch(`${API_BASE_URL}/get_opd_by_email.php?email=${encodeURIComponent(email)}`);
      const profData = await profRes.json();
      if (profData?.status === 'success' && profData?.data?.sub_city) {
        subCity = profData.data.sub_city;
        setOpdSubCity(subCity);
      } else {
        setDeletedMembers([]);
        setLoading(false);
        return;
      }
    }
    
    // Fetch all deleted members
    const response = await fetch(`${API_BASE_URL}/getDeletedMembers.php?opdEmail=${encodeURIComponent(email)}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.status === 'success') {
      const list = Array.isArray(data.members) ? data.members : [];
      // Filter by sub city
      const norm = (s) => (s || '').toString().trim().toLowerCase();
      const subCityNorm = norm(subCity);
      const filtered = list.filter((m) => norm(m.subCity) === subCityNorm);
      setDeletedMembers(filtered);
    } else {
      throw new Error(data.message || 'Failed to fetch deleted members');
    }
  } catch (error) {
    console.error('Error loading deleted members:', error);
    setError('Failed to load deleted members. Please try again.');
    toast.error(error.message || 'Failed to load deleted members');
  } finally {
    setLoading(false);
  }
};

  const handleRestore = async (id, memberName) => {
  if (!window.confirm(`Are you sure you want to restore ${memberName}?`)) {
    return;
  }

  try {
    setRestoring(id);

    const user = JSON.parse(localStorage.getItem("currentUser"));
    if (!user?.email) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/restoreMember.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, requestEmail: user.email })
    });

    const data = await response.json();

    if (!response.ok || data.status === 'error') {
      throw new Error(data.message || 'Failed to restore member');
    }

    toast.success('Member restored successfully');
    
    // Update the deletedMembers state by filtering out the restored member
    setDeletedMembers(prevMembers => 
      prevMembers.filter(member => member.id !== id)
    );

  } catch (error) {
    console.error('Restore error:', error);
    toast.error(error.message || 'Failed to restore member');
  } finally {
    setRestoring(null);
  }
};

  const handlePermanentDelete = async (member) => {
    if (!window.confirm(`This will permanently delete ${member.firstName} ${member.lastName}. This action cannot be undone. Are you sure?`)) {
      return;
    }

    setDeleting(member.id);
    
    try {
      const response = await fetch(`${API_BASE_URL}/permanentlyDeleteMember.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: member.id,
          requestEmail: currentUserEmail,
          idFile: member.idAttachment // If you have file attachments to delete
        }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        toast.success('Member permanently deleted');
        setDeletedMembers(prev => prev.filter(m => m.id !== member.id));
      } else {
        throw new Error(data.message || 'Failed to delete member');
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error(error.message || 'Failed to delete member');
    } finally {
      setDeleting(false);
    }
  };

  const filteredMembers = deletedMembers.filter(member => {
    const searchLower = searchTerm.toLowerCase();
    return (
      member.firstName?.toLowerCase().includes(searchLower) ||
      member.lastName?.toLowerCase().includes(searchLower) ||
      member.uniqueID?.toLowerCase().includes(searchLower) ||
      member.phoneNumber?.includes(searchTerm)
    );
  });

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-teal-600" />
          <p className="mt-2 text-gray-600">Loading deleted members...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => fetchDeletedMembers(currentUserEmail)}
              className="mt-2 text-sm text-teal-600 hover:text-teal-500 font-medium"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deleted Members</h1>
            <p className="text-gray-600 mt-1">
              View and manage members that have been deleted
            </p>
          </div>
          <div className="mt-6 sm:mt-8">
            <Link
              to="/members"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
            >
              <UserCheck className="w-4 h-4 mr-2" />
              View Active Members
            </Link>
          </div>
        </div>

        {/* Search and Stats */}
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                placeholder="Search by name, ID number, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="text-sm text-gray-500">
              {filteredMembers.length} {filteredMembers.length === 1 ? 'member' : 'members'} found
            </div>
          </div>
        </div>

        {/* Members Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {filteredMembers.length === 0 ? (
            <div className="text-center py-12">
              <UserX className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No deleted members found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'No members match your search.' : 'All members are currently active.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID Number
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Deleted On
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-10 h-10 w-10 flex items-center justify-center rounded-full bg-teal-100 text-teal-600">
                            <User className="h-5 w-5" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {member.firstName} {member.lastName}
                            </div>
                            <div className="text-sm text-gray-500">{member.phoneNumber}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {member.uniqueID || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {member.deletedAt ? new Date(member.deletedAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2 justify-end">
                          <button
  onClick={() => handleRestore(member.id, `${member.firstName} ${member.lastName}`)}
  disabled={restoring === member.id}
  className="text-green-600 hover:text-green-800"
>
  {restoring === member.id ? 'Restoring...' : 'Restore'}
</button>
                          <button
                            onClick={() => handlePermanentDelete(member)}
                            className="text-red-600 hover:text-red-900"
                            disabled={deleting === member.id}
                            title="Permanently delete"
                          >
                            {deleting === member.id ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                              <Trash2 className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeletedMembersPage;